import { createSlice, createAsyncThunk ,PayloadAction} from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

// ============================================================================
// TYPES
// ============================================================================

/**
 * Search option accepted by the manual time-off searchEmployees API.
 * Determines whether the search is performed against name or NUID fields.
 */
export type ManualTimeOffSearchOption = 'name' | 'nuid'

/**
 * Payload shape for the {@link searchManualTimeOffEmployees} thunk.
 * - `searchOption='name'` → provide `searchFName` (first name) and/or `searchLName` (last name)
 * - `searchOption='nuid'` → provide `searchValue` containing the employee NUID (min 2 chars)
 */
export interface ManualTimeOffSearchParams {
  /** Whether to search by employee name or NUID. */
  searchOption: ManualTimeOffSearchOption
  /** The NUID search term; used when searchOption is 'nuid'. */
  searchValue?: string
  /** First name fragment; used when searchOption is 'name'. */
  searchFName?: string
  /** Last name fragment; used when searchOption is 'name'. */
  searchLName?: string
}

/**
 * Shape of a single employee record returned by
 * `GET /tort/manual-timeoff/searchEmployees`.
 */
export interface ManualTimeOffEmployee {
  /** Employee's full name, e.g. "John M Smith". */
  name: string
  /** Employee's NUID identifier. */
  nuid: string
  /**
   * Formatted authorized approver name and NUID,
   * e.g. "DOE, JANE M (K123456)".
   */
  authorizedApprover: string | null
  /** Department name and ID, e.g. "IT Services - 12345". */
  department: string
  /** Geographic region name, e.g. "Northern California". */
  region: string
}
/* for storing the employee selected for manual time-off */
export interface SelectedEmployee {
  nuid: string
  name: string
}
/** State shape for the manualTimeOff slice. */
export interface ManualTimeOffState {
  /** True while the search API call is in-flight. */
  isLoading: boolean
  /** True once the user has submitted at least one search — controls empty-state visibility. */
  searchPerformed: boolean
  /** Employee records returned by the most recent successful search. */
  results: ManualTimeOffEmployee[]
  /** Human-readable error message when the API call fails; null otherwise. */
  error: string | null
  selectedEmployee: SelectedEmployee | null
}
const STORAGE_KEY = 'manualTimeOffEmployee'
// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ManualTimeOffState = {
  isLoading: false,
  searchPerformed: false,
  results: [],
  error: null,
  selectedEmployee: (() => {
    try {
      const employee = sessionStorage.getItem(STORAGE_KEY)
      return employee ? (JSON.parse(employee) as SelectedEmployee) : null
    } catch {
      return null
    }
  })(),
}

// ============================================================================
// THUNKS
// ============================================================================

/**
 * Searches for employees eligible for a manual time-off submission.
 *
 * GET /tort/manual-timeoff/searchEmployees
 *
 * Query params:
 * - `actionCode=searchEmployees` (constant)
 * - `searchOption` — 'name' or 'nuid'
 * - `searchValue` — name or NUID fragment (min 2 characters)
 *
 * The `x-nuid` identity header is attached automatically by the shared Axios
 * interceptor and identifies the requestor proxy performing the search.
 *
 * @param params - Search option and value.
 * @returns Array of {@link ManualTimeOffEmployee} objects.
 */
export const searchManualTimeOffEmployees = createAsyncThunk<
  ManualTimeOffEmployee[],
  ManualTimeOffSearchParams
>(
  'admin/manualTimeOff/searchEmployees',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const queryParams: Record<string, string> = {
        actionCode: 'searchEmployees',
        searchOption: payload.searchOption,
      }

      if (payload.searchOption === 'name') {
        if (payload.searchFName) queryParams.searchFName = payload.searchFName
        if (payload.searchLName) queryParams.searchLName = payload.searchLName
      } else if (payload.searchValue) {
        queryParams.searchValue = payload.searchValue
      }

      const response = await api.get<ManualTimeOffEmployee[]>(
        endpoints.admin.searchManualTimeOffEmployees,
        {
          params: queryParams,
          signal,
        }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to search employees for manual timeoff')
      )
    }
  }
)

// ============================================================================
// SLICE
// ============================================================================

const manualTimeOffSlice = createSlice({
  name: 'manualTimeOff',
  initialState,
  reducers: {
    /**
     * Resets the search form/results state back to their initial values.
     * Call this when the user navigates away from the manual time-off page.
     *
     * Deliberately does NOT touch `selectedEmployee` — that field has its own
     * lifecycle (set by setSelectedEmployee, cleared by clearSelectedEmployee)
     * and is expected to survive this reset, since it's dispatched on unmount
     * of the search page, which happens immediately after successfully
     * selecting an employee and navigating away to act on their behalf.
     * Returning the frozen `initialState` object here would revert
     * `selectedEmployee` back to whatever sessionStorage held once, at the
     * moment this module first loaded, instead of the just-selected employee.
     */
    resetManualTimeOffSearch: (state) => {
      state.isLoading = initialState.isLoading
      state.searchPerformed = initialState.searchPerformed
      state.results = initialState.results
      state.error = initialState.error
    },
 /*   * Sets the selected employee for manual time-off submission.     * Call this when the user selects an employee from the search results. */
  setSelectedEmployee: (
  state,
  action: PayloadAction<SelectedEmployee>
) => {
  state.selectedEmployee = action.payload
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(action.payload)
  )
},
//   * Clears the selected employee for manual time-off submission.     * Call this when the user navigates away from the manual time-off page or clears the selection from both redux and session. */
clearSelectedEmployee: (state) => {
  state.selectedEmployee = null
  sessionStorage.removeItem(STORAGE_KEY)
},
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchManualTimeOffEmployees.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(searchManualTimeOffEmployees.fulfilled, (state, action) => {
        state.isLoading = false
        state.searchPerformed = true
        state.results = action.payload
      })
      .addCase(searchManualTimeOffEmployees.rejected, (state, action) => {
        state.isLoading = false
        state.searchPerformed = true
        state.results = []
        state.error = action.payload as string
      })
  },
})

export const { resetManualTimeOffSearch, setSelectedEmployee, clearSelectedEmployee } = manualTimeOffSlice.actions
export default manualTimeOffSlice.reducer
